import React, { ReactElement } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import loadable from '@loadable/component';
import {
  RouteAuthenticator,
  routeAuthenticatorRouteProps,
  UnauthenticatedRoute as Route,
  AuthenticatedRoute,
} from '@blocs.auth';
import { useTranslation } from '@blocs.i18n';
import { useRemoveSplashLoader } from '@minecraft.utils';
import Loader from '@minecraft.loader';

import PrivatePage from 'components/core/PrivatePage';
import PublicPage from 'components/core/PublicPage';
import { TALENT_SCOUT_ROUTE } from 'components/TalentScout/TalentScout';

const ProjectsDetail = loadable(() => import(/* webpackChunkName: "reps-proj-details" */ 'src/routes/projects/detail'));
const NotificationSettings = loadable(
  () => import(/* webpackChunkName: "reps-notif-settings" */ 'components/AccountSettings/NotificationSettings')
);
const ProjectNotificationSettings = loadable(
  () => import(/* webpackChunkName: "reps-notif-settings" */ 'components/AccountSettings/ProjectNotificationSettings')
);
const Administrative = loadable(
  () => import(/* webpackChunkName: "reps-admin-settings" */ 'components/AccountSettings/Administrative')
);
const AccountDetails = loadable(
  () => import(/* webpackChunkName: "reps-acc-details" */ 'components/AccountSettings/AccountDetails')
);
const ProjectsRolesSubmit = loadable(
  () => import(/* webpackChunkName: "reps-proj-roles-submit" */ 'src/routes/projects/roles/submit')
);

const ProfileUploadPhoto = loadable(
  () => import(/* webpackChunkName: "reps-profile-upload-photo" */ 'src/routes/profile/upload-photo')
);
const ProfileUploadAudio = loadable(
  () => import(/* webpackChunkName: "reps-profile-upload-audio" */ 'src/routes/profile/upload-audio')
);
const ProfileUploadVideo = loadable(
  () => import(/* webpackChunkName: "reps-profile-upload-video" */ 'src/routes/profile/upload-video')
);
const ProfileUploadMedia = loadable(
  () => import(/* webpackChunkName: "reps-profile-upload-media" */ 'src/routes/profile/upload-media')
);
const ReviewMedia = loadable(
  () => import(/* webpackChunkName: "reps-review-media" */ 'src/routes/profile/review-media')
);
const ProfileCreate = loadable(() => import(/* webpackChunkName: "reps-profile-create" */ 'src/routes/profile/create'));
const ProfileEdit = loadable(() => import(/* webpackChunkName: "reps-profile-edit" */ 'src/routes/profile/edit'));
const Profile = loadable(() => import(/* webpackChunkName: "reps-profile" */ 'src/routes/profile'));
const Projects = loadable(() => import(/* webpackChunkName: "reps-project-list" */ 'src/routes/projects'));
const PackagesView = loadable(
  () => import(/* webpackChunkName: "reps-packages-view" */ 'src/routes/packages/packagesView')
);
const PackageOverview = loadable(
  () => import(/* webpackChunkName: "reps-packages-overview" */ 'src/routes/packages/packageOverview')
);
const PackageViewEditFolder = loadable(
  () => import(/* webpackChunkName: "reps-package-view-edit-folder" */ 'src/routes/packages/packageViewEditFolder')
);
const PackageFolderAddTalents = loadable(
  () => import(/* webpackChunkName: "reps-package-folder-add-tal" */ 'src/routes/packages/packageFolderAddTalents')
);
const RecipientView = loadable(
  () => import(/* webpackChunkName: "reps-recipient-view" */ 'src/routes/recipient/recipientView')
);
const RecipientGridView = loadable(
  () => import(/* webpackChunkName: "reps-recipient-grid-view" */ 'src/routes/recipient/recipientGridView')
);
const RecipientDetailView = loadable(
  () => import(/* webpackChunkName: "reps-recipient-detail-view" */ 'src/routes/recipient/recipientDetailView')
);
const Roster = loadable(() => import(/* webpackChunkName: "reps-roster" */ 'src/routes/roster/roster'));
const RosterInvitationHistory = loadable(
  () => import(/* webpackChunkName: "reps-invite-history" */ 'src/routes/roster/roster-invitation-history')
);
const RosterLists = loadable(() => import(/* webpackChunkName: "reps-roster-lists" */ 'src/routes/roster/roster-list'));
const RosterListView = loadable(
  () => import(/* webpackChunkName: "reps-roster-list-view" */ 'src/routes/roster/roster-list-view')
);
const RosterListsAddTalent = loadable(
  () => import(/* webpackChunkName: "reps-roster-lists-add-talent" */ 'src/routes/roster/roster-list-add-talent')
);
const RosterInviteTalent = loadable(
  () => import(/* webpackChunkName: "reps-invite-talent" */ 'src/routes/roster/invite/talent')
);
const TalentScout = loadable(() => import(/* webpackChunkName: "reps-talent-scout" */ 'src/routes/talent-scout'));
const Worksheet = loadable(() => import(/* webpackChunkName: "reps-worksheet" */ 'src/components/Worksheet'));
const ProfileMedia = loadable(
  () => import(/* webpackChunkName: "reps-profile-media" */ 'components/Profile/ProfileMedia')
);
const Maintenance = loadable(
  () => import(/* webpackChunkName: "reps-maintenance" */ 'src/routes/splash-screens/maintenance')
);

const Reports = loadable(() => import(/* webpackChunkName: "reps-reports-form" */ 'src/routes/reports/reports'));

const ReportSingleTalentReport = loadable(
  () => import(/* webpackChunkName: "reps-report-single-talent" */ 'src/routes/reports/singleTalentReport')
);

const ReportAllTalentReport = loadable(
  () => import(/* webpackChunkName: "reps-report-all-talent" */ 'src/routes/reports/allTalentReport')
);

const ReportProjectResult = loadable(
  () => import(/* webpackChunkName: "reps-reports-project-result" */ 'src/routes/reports/projectResult')
);

const ReportProjectSubmissionsResult = loadable(
  () => import(/* webpackChunkName: "reps-reports-project-submissions" */ 'src/routes/reports/projectSubmissionsResult')
);

const NotFound = loadable(() => import(/* webpackChunkName: "reps-not-found" */ '@blocs.not-found'), {
  resolveComponent: (components) => components.NotFound,
});

const Appointments = loadable(() => import(/* webpackChunkName: "reps-appointments" */ 'src/components/Appointments'));

const Routes = (): ReactElement => {
  const { t } = useTranslation();

  useRemoveSplashLoader();

  return (
    <Switch>
      <Route exact path="/maintenance">
        <Maintenance fallback={<Loader />} />
      </Route>
      <Route hideZendesk exact path="/:guid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})">
        <PublicPage>
          <RecipientView />
        </PublicPage>
      </Route>
      <Route
        hideZendesk
        exact
        path="/:guid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/folder/:folderId/talents"
      >
        <PublicPage>
          <RecipientGridView />
        </PublicPage>
      </Route>
      <Route
        hideZendesk
        exact
        path="/:guid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/folder/:folderId/talents/:talentGuid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"
      >
        <PublicPage>
          <RecipientDetailView />
        </PublicPage>
      </Route>
      <Route {...routeAuthenticatorRouteProps}>
        <RouteAuthenticator redirectTo="/projects" />
      </Route>
      <Route exact path="/">
        <PrivatePage pageTitle={t('common:form.label.projects')}>
          <Redirect to="projects" />
        </PrivatePage>
      </Route>

      <PrivatePage exact path="/projects" pageTitle={t('common:form.label.projects')}>
        <Projects fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/projects/:projectId" pageTitle={t('reps:pageTitle.projectOverview')}>
        <ProjectsDetail fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/projects/:projectId/appointments" pageTitle={t('reps:pageTitle.projectOverview')}>
        <Appointments fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/projects/:projectId/appointments/raw"
        pageTitle={t('reps:pageTitle.projectOverview')}
        hideHeader
      >
        <Appointments fallback={<Loader />} raw />
      </PrivatePage>
      <PrivatePage
        exact
        path="/projects/:projectId/roles/:roleId/submit"
        pageTitle={t('reps:pageTitle.selectAndSubmit')}
        color="white"
        fixedWidth={false}
      >
        <ProjectsRolesSubmit fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/projects/:projectId/roles/:roleId/profile/:profileId/customize"
        pageTitle={t('reps:pageTitle.selectAndSubmit')}
        color="white"
        fixedWidth={false}
      >
        <ProjectsRolesSubmit fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/packages" pageTitle={t('reps:pageTitle.packages')}>
        <PackagesView fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/packages/:packageId" pageTitle={t('common:label.package')} color="white">
        <PackageOverview fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/packages/:packageId/folders/:folderId" pageTitle={t('common:label.folder')}>
        <PackageViewEditFolder fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/packages/:packageId/folders/:folderId/add-talents"
        pageTitle={t('reps:pageTitle.addTalentToFolder')}
      >
        <PackageFolderAddTalents fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/profile/:profileId/upload-photo"
        pageTitle={t('reps:pageTitle.uploadPhoto')}
        color="white"
      >
        <ProfileUploadPhoto fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/profile/:profileId/upload-video"
        pageTitle={t('reps:pageTitle.uploadVideo')}
        color="white"
      >
        <ProfileUploadVideo fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/profile/:profileId/upload-audio"
        pageTitle={t('reps:pageTitle.uploadAudio')}
        color="white"
      >
        <ProfileUploadAudio fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/profile/:profileId/upload-media"
        pageTitle={t('reps:pageTitle.uploadMedia')}
        color="white"
      >
        <ProfileUploadMedia fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/profile/:profileId/upload-media/review-media"
        pageTitle={t('reps:pageTitle.reviewMedia')}
        color="white"
      >
        <ReviewMedia fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/profile/create" pageTitle={t('reps:pageTitle.createNewProfile')}>
        <ProfileCreate fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/profile/:profileId" pageTitle={t('reps:pageTitle.profile')}>
        <Profile fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/profile/:profileId/:editView" pageTitle={t('reps:pageTitle.profileEdit')}>
        <ProfileEdit fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/profile/:profileId/artist/:artistId/media" pageTitle={t('reps:pageTitle.talentMedia')}>
        <ProfileMedia fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/roster" pageTitle={t('reps:pageTitle.talentRoster')} fixedWidth={false}>
        <Roster fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/roster/invitation/history" pageTitle={t('reps:pageTitle.invitationHistory')}>
        <RosterInvitationHistory fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/roster/roster-lists" pageTitle={t('reps:pageTitle.lists')}>
        <RosterLists fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/roster/roster-lists/:id" pageTitle={t('reps:pageTitle.rosterLists')}>
        <RosterListView fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage
        exact
        path="/roster/roster-lists/:id/add-talent"
        pageTitle={t('reps:pageTitle.rosterListsAddTalent')}
      >
        <RosterListsAddTalent fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/roster/invite/talent" pageTitle={t('reps:pageTitle.inviteTalent')}>
        <RosterInviteTalent fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage path={TALENT_SCOUT_ROUTE} pageTitle={t('common:form.label.projects')}>
        <TalentScout fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/worksheet" pageTitle={t('reps:pageTitle.worksheet')} fixedWidth={false}>
        <Worksheet fallback={<Loader />} />
      </PrivatePage>
      <PrivatePage exact path="/account-settings/notifications" pageTitle={t('reps:pageTitle.notificationSettings')}>
        <NotificationSettings />
      </PrivatePage>
      <PrivatePage
        exact
        path="/account-settings/notifications/projects"
        pageTitle={t('reps:pageTitle.notificationSettings')}
      >
        <ProjectNotificationSettings />
      </PrivatePage>
      <PrivatePage exact path="/account-settings/administrative" pageTitle={t('reps:pageTitle.administrative')}>
        <Administrative />
      </PrivatePage>
      <PrivatePage exact path="/account-settings/details" pageTitle={t('common:title.accountDetails')}>
        <AccountDetails />
      </PrivatePage>

      {/* These routes dont use the header and layout applied by PrivatePage, but still require authentication */}
      <AuthenticatedRoute exact path="/reports/single-talent/:reportId">
        <ReportSingleTalentReport />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/reports/all-talent/:reportId">
        <ReportAllTalentReport />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/reports/project/:reportId">
        <ReportProjectResult />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/reports/submissions/:reportId">
        <ReportProjectSubmissionsResult />
      </AuthenticatedRoute>
      <PrivatePage exact path="/reports" pageTitle={t('reps:pageTitle.reports')}>
        <Reports />
      </PrivatePage>
      <PrivatePage path="*" pageTitle={t('reps:pageTitle.notFound')}>
        <NotFound fallback={<Loader />} />
      </PrivatePage>
    </Switch>
  );
};

export default Routes;
